import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Paper,
  TableSortLabel,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useApiCaller from 'hooks/apiCaller';

const NewTableComponent = ({
  // Data props
  tableData = [],
  columns = [],
  tableName = 'Table',

  // Pagination props
  count = 0,
  page = 0,
  limit = 10,
  onPageChange,
  onLimitChange,

  // Search and filter props
  enableSearch = true,
  enableFilters = true,
  searchPlaceholder = 'Search...',
  filters = [],

  // Action props
  onRowClick,
  rowClickedKey = '_id',
  navigationPath,
  useWholeRowClicked = true,

  // Export props
  enableExport = false,
  exportFunction,
  exportFileName = 'export.xlsx',

  // UI props
  loading = false,
  error = null,
  emptyMessage = 'No data available',
  getRowStyle,
  customHeaderActions,
  customRowActions,

  // Styling props
  elevation = 2,
  borderRadius = 2,
  maxHeight = '600px',
}) => {
  const navigate = useNavigate();
  const apiCaller = useApiCaller();

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [activeFilters, setActiveFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [localPage, setLocalPage] = useState(1);
  const [localLimit, setLocalLimit] = useState(10);
  const debounceRef = useRef(null);

  // Debounce search input (300ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setLocalPage(1); // reset to page 1 on new search
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  // Sync local state with props when they change
  useEffect(() => {
    if (!onPageChange) {
      setLocalPage(1);
      setLocalLimit(10);
    }
  }, [onPageChange]);

  // Reset to page 1 when tableData changes (e.g. tab switch)
  useEffect(() => {
    if (!onPageChange) {
      setLocalPage(1);
    }
  }, [tableData]);

  // Computed values
  const filteredData = useMemo(() => {
    let data = [...tableData];

    // Apply debounced search across all string fields
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      data = data.filter((row) =>
        Object.values(row).some((val) => typeof val === 'string' && val.toLowerCase().includes(q))
      );
    }

    // Apply filters
    Object.entries(activeFilters).forEach(([field, value]) => {
      if (value && value !== 'all') {
        data = data.filter((row) => row[field] === value);
      }
    });

    return data;
  }, [tableData, debouncedSearch, activeFilters]);

  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;

    return [...filteredData].sort((a, b) => {
      // Find the column configuration for the sort field
      const column = columns.find((col) => col.field === sortField);

      // Use sortValue function if available, otherwise use raw field value
      const aValue = column?.sortValue ? column.sortValue(a) : a[sortField];
      const bValue = column?.sortValue ? column.sortValue(b) : b[sortField];

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortField, sortDirection, columns]);

  // Client-side pagination
  const paginatedData = useMemo(() => {
    // If onPageChange is provided, it's server-side pagination — data already sliced by server
    if (onPageChange) {
      return sortedData;
    }
    // Client-side: slice data for current page
    const start = (localPage - 1) * localLimit;
    return sortedData.slice(start, start + localLimit);
  }, [sortedData, onPageChange, localPage, localLimit]);

  // Determine the total count for pagination
  const totalCount = useMemo(() => {
    // If count is provided and onPageChange exists, use server-side count
    if (count > 0 && onPageChange) {
      return count;
    }
    // Otherwise, use the length of filtered/sorted data for client-side pagination
    return sortedData.length;
  }, [count, sortedData.length, onPageChange]);

  // Event handlers
  const handlePageChange = (event, newPage) => {
    if (onPageChange) {
      // Server-side pagination
      onPageChange(newPage + 1, limit);
    } else {
      // Client-side pagination - update local state
      setLocalPage(newPage + 1);
    }
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    if (onLimitChange) {
      // Server-side pagination
      onLimitChange(newLimit);
    } else {
      // Client-side pagination - update local state
      setLocalLimit(newLimit);
      setLocalPage(1); // Reset to first page
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleFilterChange = (field, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setActiveFilters({});
    setSortField(null);
    setSortDirection('asc');
    setLocalPage(1);
  };

  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick(row);
    } else if (navigationPath) {
      const id = row._id ?? row[rowClickedKey];
      navigate(`${navigationPath}${id}`);
    }
  };

  const handleExport = async () => {
    if (exportFunction) {
      try {
        await exportFunction(sortedData);
      } catch (error) {
        console.error('Export failed:', error);
      }
    }
  };

  const handleRefresh = () => {
    if (onPageChange) {
      onPageChange(1, limit);
    }
  };

  // Render functions
  const renderCell = (row, column) => {
    if (column.render) {
      return column.render(row);
    }

    const value = row[column.field];

    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'boolean') {
      return (
        <Chip
          label={value ? 'Yes' : 'No'}
          color={value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      );
    }

    if (typeof value === 'string' && value.length > 50) {
      return (
        <Tooltip title={value}>
          <Typography variant="body2" noWrap>
            {value.substring(0, 50)}...
          </Typography>
        </Tooltip>
      );
    }

    return String(value);
  };

  const renderLoadingRows = () => {
    return Array.from({ length: limit }).map((_, index) => (
      <TableRow key={`loading-${index}`}>
        {columns.map((column, colIndex) => (
          <TableCell key={colIndex}>
            <Skeleton animation="wave" />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  const renderEmptyState = () => (
    <TableRow>
      <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </TableCell>
    </TableRow>
  );

  return (
    <Paper
      elevation={elevation}
      sx={{
        borderRadius: borderRadius,
        overflow: 'hidden',
      }}
    >
      {/* Header Actions */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          flexWrap="wrap"
        >
          <Typography variant="h6" fontWeight="medium">
            {tableName}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            {customHeaderActions}

            {enableSearch && (
              <TextField
                size="small"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: '100%', sm: 250 } }}
              />
            )}

            {enableFilters && filters.length > 0 && (
              <Tooltip title="Filters">
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                >
                  <FilterIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            {enableExport && (
              <Tooltip title="Export">
                <IconButton onClick={handleExport} disabled={!sortedData.length}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Filters Section */}
        {showFilters && filters.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {filters.map((filter) => (
                <FormControl key={filter.field} size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={activeFilters[filter.field] || 'all'}
                    onChange={(e) => handleFilterChange(filter.field, e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="all">{filter.label}</MenuItem>
                    {filter.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
              <Button size="small" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                Clear
              </Button>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Box sx={{ maxHeight, overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  sortDirection={sortField === column.field ? sortDirection : false}
                  sx={{
                    backgroundColor: 'background.paper',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={sortField === column.field}
                      direction={sortField === column.field ? sortDirection : 'asc'}
                      onClick={() => handleSort(column.field)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? renderLoadingRows()
              : paginatedData.length === 0
                ? renderEmptyState()
                : paginatedData.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      onClick={() => useWholeRowClicked && handleRowClick(row)}
                      sx={{
                        cursor: useWholeRowClicked ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        ...(getRowStyle ? getRowStyle(row) : {}),
                      }}
                    >
                      {columns.map((column, colIndex) => (
                        <TableCell key={colIndex}>{renderCell(row, column)}</TableCell>
                      ))}
                    </TableRow>
                  ))}
          </TableBody>
        </Table>
      </Box>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalCount}
        page={onPageChange ? page - 1 : localPage - 1}
        onPageChange={handlePageChange}
        rowsPerPage={onLimitChange ? limit : localLimit}
        onRowsPerPageChange={handleLimitChange}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      />
    </Paper>
  );
};

export default NewTableComponent;
